import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { api } from '../api';
import { toast } from 'react-toastify';

function Announcements() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState('MAINTENANCE'); // Default type
  const [audience, setAudience] = useState('SELLER'); // Default audience
  const [imageUrl, setImageUrl] = useState('');
  const [link, setLink] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5); // Display 5 items per page
  const [totalAnnouncements, setTotalAnnouncements] = useState(0);
  const [noOfPages, setNoOfPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [editingAnnouncementId, setEditingAnnouncementId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, [page, limit]);

  async function fetchAnnouncements() {
    setLoading(true);
    try {
      const { announcements, total, noOfPages: pages } = await api.getAnnouncements(page, limit);
      setItems(announcements);
      setTotalAnnouncements(total);
      setNoOfPages(pages);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      toast.error("Failed to load announcements.");
    }
    setLoading(false);
  }

  async function handlePublish(e) {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      toast.error("Title and Content cannot be empty.");
      return;
    }

    setSubmitting(true);

    const payload = {
      message: content,
      type,
      audience,
      imageUrl: imageUrl || null,
      link: link || null,
      scheduleDate: scheduleDate ? new Date(scheduleDate).toISOString() : null,
    };

    try {
      let response;
      if (editingAnnouncementId) {
        response = await api.updateAnnouncement(editingAnnouncementId, payload);
      } else {
        response = await api.createAnnouncement(payload);
      }
      
      if (response.ok) {
        toast.success(response.message || `Announcement ${editingAnnouncementId ? 'updated' : 'created'} successfully!`);
        fetchAnnouncements();
        setTitle('');
        setContent('');
        setImageUrl('');
        setLink('');
        setScheduleDate('');
        setType('MAINTENANCE');
        setAudience('SELLER');
        setEditingAnnouncementId(null); // Exit editing mode
      } else {
        const errorMessage = Array.isArray(response.message) ? response.message.join(', ') : response.message || `Failed to ${editingAnnouncementId ? 'update' : 'create'} announcement.`;
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error(`Error ${editingAnnouncementId ? 'updating' : 'creating'} announcement:`, error);
      toast.error(`An error occurred while ${editingAnnouncementId ? 'updating' : 'creating'} the announcement.`);
    } finally {
      setSubmitting(false);
    }
  }

  function handleEditClick(announcement) {
    setTitle(announcement.message);
    setContent(announcement.message);
    setType(announcement.type);
    setAudience(announcement.audience);
    setImageUrl(announcement.imageUrl || '');
    setLink(announcement.link || '');
    setScheduleDate(announcement.scheduleDate ? new Date(announcement.scheduleDate).toISOString().slice(0, 16) : '');
    setEditingAnnouncementId(announcement.id);
  }

  function handleCancelEdit() {
    setTitle('');
    setContent('');
    setImageUrl('');
    setLink('');
    setScheduleDate('');
    setType('MAINTENANCE');
    setAudience('SELLER');
    setEditingAnnouncementId(null);
  }

  return (
    <>
      <Helmet><title>Announcements | Owena Admin Dashboard</title></Helmet>
      <div className="ec-content-wrapper">
        <div className="content">
          <div className="breadcrumb-wrapper breadcrumb-contacts">
            <div>
              <h1>Announcements</h1>
              <p className="breadcrumbs"><span><a href="#" onClick={(e)=>e.preventDefault()}>Home</a></span><span><i className="mdi mdi-chevron-right"></i></span>Promotions</p>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-6">
              <div className="card card-default">
                <div className="card-header"><h2>{editingAnnouncementId ? 'Edit Announcement' : 'Create Announcement'}</h2></div>
                <div className="card-body">
                  <form onSubmit={handlePublish}>
                    <div className="mb-3"><label className="form-label">Title</label><input className="form-control" value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="Enter title" /></div>
                    <div className="mb-3"><label className="form-label">Content</label><textarea className="form-control" rows={6} value={content} onChange={(e)=>setContent(e.target.value)} placeholder="Enter content" /></div>
                    <div className="mb-3"><label className="form-label">Type</label><select className="form-select" value={type} onChange={(e)=>setType(e.target.value)}><option value="MAINTENANCE">Maintenance</option><option value="PROMOTION">Promotion</option><option value="UPDATE">Update</option><option value="GENERAL">General</option></select></div>
                    <div className="mb-3"><label className="form-label">Audience</label><select className="form-select" value={audience} onChange={(e)=>setAudience(e.target.value)}><option value="SELLER">Seller</option><option value="CUSTOMER">Customer</option><option value="ALL">All</option></select></div>
                    <div className="mb-3"><label className="form-label">Image URL (optional)</label><input className="form-control" value={imageUrl} onChange={(e)=>setImageUrl(e.target.value)} placeholder="Enter image URL" /></div>
                    <div className="mb-3"><label className="form-label">Link (optional)</label><input className="form-control" value={link} onChange={(e)=>setLink(e.target.value)} placeholder="Enter link" /></div>
                    <div className="mb-3"><label className="form-label">Schedule Date (optional)</label><input className="form-control" type="datetime-local" value={scheduleDate} onChange={(e)=>setScheduleDate(e.target.value)} /></div>
                    <button type="submit" className="btn btn-primary me-2" disabled={submitting}>{submitting ? (editingAnnouncementId ? 'Updating...' : 'Publishing...') : (editingAnnouncementId ? 'Update Announcement' : 'Publish')}</button>
                    {editingAnnouncementId && <button type="button" className="btn btn-secondary" onClick={handleCancelEdit} disabled={submitting}>Cancel Edit</button>}
                  </form>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card card-default">
                <div className="card-header"><h2>Recent Announcements</h2></div>
                <div className="card-body">
                  {loading ? <p>Loading announcements...</p> : items.length === 0 ? <p>No announcements yet.</p> : (
                    <div className="list-group">
                      {items.map(a => (
                        <a key={a.id} href="#" onClick={(e)=>e.preventDefault()} className="list-group-item list-group-item-action">
                          <div className="d-flex w-100 justify-content-between">
                            <h5 className="mb-1">{a.message}</h5>
                            <small>{new Date(a.createdAt).toLocaleString()}</small>
                          </div>
                          <p className="mb-1">Type: {a.type} | Audience: {a.audience}</p>
                          {a.link && <small className="mb-1 d-block text-truncate"><a href={a.link} target="_blank" rel="noreferrer">{a.link}</a></small>}
                          {a.imageUrl && <img src={a.imageUrl} alt="Announcement" className="img-fluid mt-2" style={{ maxWidth: '100px' }} />}
                          <div className="mt-2 text-end">
                            <button className="btn btn-sm btn-info" onClick={() => handleEditClick(a)}>Edit</button>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                  {noOfPages > 1 && (
                    <nav className="mt-3">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}><a className="page-link" href="#" onClick={(e) => { e.preventDefault(); setPage(prev => Math.max(1, prev - 1)); }}>Previous</a></li>
                        {[...Array(noOfPages)].map((_, idx) => (
                          <li key={idx + 1} className={`page-item ${page === idx + 1 ? 'active' : ''}`}><a className="page-link" href="#" onClick={(e) => { e.preventDefault(); setPage(idx + 1); }}>{idx + 1}</a></li>
                        ))}
                        <li className={`page-item ${page === noOfPages ? 'disabled' : ''}`}><a className="page-link" href="#" onClick={(e) => { e.preventDefault(); setPage(prev => Math.min(noOfPages, prev + 1)); }}>Next</a></li>
                      </ul>
                    </nav>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Announcements;
